import React, { useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import { connect } from 'react-redux';
import { useAnalytics } from 'hooks';
import { withAuth, withHuggg, withRedirect, withLocations } from 'hoc';
import { Layout } from 'layouts';
import { Huggg, Reaction } from 'components';
import { PageTitle } from 'components/misc';
import { makeTitle, getBasePathname } from 'utilities/huggg';
import styles from './landing.module.scss';
import {
  GiftWrap,
  Divider,
  PrimaryButton,
  useThemeContext,
} from '@huggg/react-components';
import { Link } from 'react-router-dom';
import {
  setHugggUnwrapped,
  isHugggUnwrapped,
  getVoucherExpiry,
  hasVoucherToClaim,
  getSegments,
  isBlueHuggg,
  hasFulfilledVoucher,
  isVoucherV2,
} from 'redux/modules/huggg';
import { ExpirationDate, PoweredByHuggg } from 'components/ui';
import { clearError, generate as generateVoucher } from 'redux/modules/voucher';
import { getRedemptionMethodID } from 'utilities/redemption';
import HistoryService from 'services/HistoryService';
import dictionary from 'config/dictionary';

const history = new HistoryService();

export const ScreensLanding = (props) => {
  const {
    huggg,
    isAuthed,
    showDetails,
    setHugggUnwrapped,
    isHugggUnwrapped,
    voucherExpiry,
    hasVoucherToClaim,
    generateVoucher,
    isBlueHuggg,
    hasFulfilledVoucher,
    isVoucherV2,
  } = props;

  const { shouldShowPoweredByHuggg, isWelfare } = useThemeContext();

  const analytics = useAnalytics();
  const backAction = useCallback(() => '/', []);
  const [isAccepting, setIsAccepting] = useState(false);

  const hugggTitle = useMemo(() => {
    return makeTitle(huggg);
  }, [huggg]);

  useEffect(() => {
    huggg.isSuperhuggg
      ? analytics.screenViewedSuperhugggLanding()
      : analytics.screenViewedHugggLanding();
  }, [analytics, huggg]);

  const onMoreInfoClicked = useCallback(() => {
    huggg.isSuperhuggg
      ? analytics.buttonClickSuperhugggActionsMoreInfo()
      : analytics.buttonClickHugggActionsMoreInfo();
  }, [analytics, huggg.isSuperhuggg]);

  const onAcceptVoucherClicked = useCallback(() => {
    setIsAccepting(true);
    generateVoucher(
      huggg.id,
      null, // no segment chosen
      getRedemptionMethodID(huggg),
      {}, // no data given
      'accept', // accept the gift / voucher
    )
      .then(() => {
        // Navigate to more-information page after voucher is generated
        history.push(`${getBasePathname(huggg)}/more-information`);
      })
      .catch(() => {
        setIsAccepting(false);
      });
  }, [huggg, generateVoucher]);

  const renderGiftUnwrapContent = () => (
    <div data-testid="giftwrap" className={styles.giftwrap}>
      <GiftWrap
        onUnwrapComplete={setHugggUnwrapped}
        senderMessage={hugggTitle}
        autoUnwrap={true}
      />
    </div>
  );

  const renderButton = () => {
    if (hasVoucherToClaim || isBlueHuggg || hasFulfilledVoucher) {
      return (
        <Link
          onClick={onMoreInfoClicked}
          to={`${getBasePathname(huggg)}/more-information`}
          style={{ textDecoration: 'none' }}
        >
          <PrimaryButton
            label={
              isBlueHuggg
                ? dictionary.landingPage.seeDetailsButton
                : dictionary.landingPage.giftDetailsButton
            }
            className={styles.moreInfoButton}
          />
        </Link>
      );
    }

    return (
      <PrimaryButton
        onClick={onAcceptVoucherClicked}
        label={
          huggg.isSuperhuggg
            ? dictionary.nextButton
            : dictionary.landingPage.acceptHugggButton
        }
        className={styles.acceptVoucherButton}
        disabled={isAccepting}
      />
    );
  };

  const renderPageContent = () => {
    return (
      <>
        <div className="container" data-test-id="landing-screen-huggg">
          <Huggg huggg={huggg} data-test-id="landing-screen-huggg" />

          <Reaction animate reactionDisabled />

          {shouldShowPoweredByHuggg && <PoweredByHuggg />}

          <Divider />

          {renderButton()}

          <ExpirationDate
            huggg={huggg}
            voucherExpiry={voucherExpiry}
            isVoucherV2={isVoucherV2}
            hasFulfilledVoucher={hasFulfilledVoucher}
          />
        </div>
      </>
    );
  };

  return (
    <>
      <PageTitle huggg={huggg} />
      <Layout
        backAction={backAction}
        showBackButton={
          isAuthed &&
          !huggg.isSuperhuggg &&
          Boolean(history.getPreviousPathname())
        }
        showDetails={showDetails}
      >
        {/* We don't want to see Giftwrap for welfare, if the huggg has already been unwrapped or if there is a voucher that has already been interacted with */}
        {isWelfare ||
        isHugggUnwrapped ||
        hasVoucherToClaim ||
        hasFulfilledVoucher
          ? renderPageContent()
          : renderGiftUnwrapContent()}
      </Layout>
    </>
  );
};

ScreensLanding.propTypes = {
  huggg: PropTypes.object.isRequired,
  isAuthed: PropTypes.bool.isRequired,
  isMultiRedeemable: PropTypes.bool.isRequired,
  isTestHuggg: PropTypes.bool.isRequired,
  showDetails: PropTypes.bool,
  setHugggUnwrapped: PropTypes.func.isRequired,
  isHugggUnwrapped: PropTypes.bool.isRequired,
  voucherExpiry: PropTypes.string,
  hasVoucherToClaim: PropTypes.bool.isRequired,
  locations: PropTypes.array.isRequired,
  isStashed: PropTypes.bool.isRequired,
  clearError: PropTypes.func.isRequired,
  segments: PropTypes.array.isRequired,
  generateVoucher: PropTypes.func.isRequired,
  isBlueHuggg: PropTypes.bool.isRequired,
  hasFulfilledVoucher: PropTypes.bool.isRequired,
  isVoucherV2: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  isHugggUnwrapped: isHugggUnwrapped(state),
  hasVoucherToClaim: hasVoucherToClaim(state),
  voucherExpiry: getVoucherExpiry(state),
  showDetails: true,
  segments: getSegments(state),
  isBlueHuggg: isBlueHuggg(state),
  hasFulfilledVoucher: hasFulfilledVoucher(state),
  isVoucherV2: isVoucherV2(state),
});

const redirectHandler = (props) => {
  const { huggg, isRedeemed, isFetchedHuggg } = props;
  const { isSuperhuggg } = huggg;

  const shouldRedirect = !isSuperhuggg && (isRedeemed || isFetchedHuggg);

  return [shouldRedirect, `${getBasePathname(props.huggg)}/redeemed`];
};

const augmentedWithHuggg = (screen) =>
  withHuggg(screen, { forceRefresh: { huggg: false } });

export default compose(
  withAuth,
  augmentedWithHuggg,
  withLocations,
  connect(mapStateToProps, { setHugggUnwrapped, clearError, generateVoucher }),
  withRedirect(redirectHandler),
)(ScreensLanding);
